import React, { useState, useEffect } from 'react';
import { Select, Button, Checkbox, Table, message, Input } from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './ImageManagementPage.css';

const { Option } = Select;

const ImageManagementPage = () => {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [showConfirmedImages, setShowConfirmedImages] = useState(false);
  const [sortByLastModify, setSortByLastModify] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const [imageId, setImageId] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [sortState, setSortState] = useState({ field: null, order: null });

  // Bulk selection
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const loadAgencies = async () => {
    setLoadingAgencies(true);
    const url = `${BASE_URL}/assessment_image/agency_list`;
    try {
      const response = await axios.get(url);
      if (response.data && Array.isArray(response.data)) {
        setAgencies(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAgencies(false);
    }
  };

  const buildSearchParams = (overrides = {}) => {
    const params = {
      sightingsCountMin: 1
    };
    if (selectedAgency) params.agency = selectedAgency;
    if (imageId && imageId.trim()) params.imageId = imageId.trim();
    if (selectedStatus) params.pursuable = selectedStatus === 'pending';
    params.confirmed = showConfirmedImages;
    if (sortByLastModify) {
      params.sort = 'lastModify,desc';
      params.excludeNullDates = true;
    } else if (sortState.field && sortState.order) {
      params.sort = `${sortState.field},${sortState.order}`;
    } else {
      params.sort = 'sightingsCount,desc';
    }
    return { ...params, ...overrides };
  };

  const searchOriginalImages = async (params = {}) => {
    setLoading(true);

    const defaultParams = {
      page: 0,
      size: 20,
      sort: 'sightingsCount,desc',
      sightingsCountMin: 1,
      ...params
    };

    Object.keys(defaultParams).forEach(
      (key) => defaultParams[key] === undefined && delete defaultParams[key]
    );

    const queryString = new URLSearchParams(defaultParams).toString();
    const url = `${BASE_URL}/original_image/search?${queryString}`;

    try {
      const response = await axios.get(url);
      const data = response.data;

      if (data && data.content) {
        const transformedData = data.content.map((item, index) => ({
          key: item.id || index,
          imageId: item.imageId,
          image: item.url,
          sightingsCount: item.sightingsCount,
          agency: item.agency,
          fileName: item.fileName,
          pursuable: item.pursuable,
          status: item.pursuable ? 'pending' : 'approved',
          notIdenticalSightingsCount: item.notIdenticalSightingsCount,
          notIdenticalRatio: item.notIdenticalRatio ?? null,
          confirmed: item.confirmed || false
        }));

        setTableData(transformedData);
        setPagination((prev) => ({
          ...prev,
          total: data.totalElements,
          current: data.number + 1
        }));
      }
    } catch (error) {
      console.error('Request failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchOriginalImages({ pursuable: true, confirmed: false });
    loadAgencies();
  }, []);

  const handleSearch = () => {
    setSortState({ field: null, order: null });
    const params = buildSearchParams({ page: 0, size: pagination.pageSize });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const handleAgencyChange = (value) => setSelectedAgency(value);
  const handleFilterStatusChange = (value) => setSelectedStatus(value);

  const handleSortByLastModifyChange = (checked) => {
    setSortByLastModify(checked);
    setSortState({ field: null, order: null });
    const params = buildSearchParams({ page: 0, size: pagination.pageSize });
    if (checked) {
      params.sort = 'lastModify,desc';
      params.excludeNullDates = true;
    } else {
      delete params.sort;
      delete params.excludeNullDates;
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const updatePursuableStatus = async (ids, pursuable) => {
    const response = await axios.post(
      `${BASE_URL}/original_image/update-pursuable`,
      {
        ids,
        pursuable
      }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update pursuable status');
  };

  const updateConfirmedStatus = async (ids, confirmed) => {
    const response = await axios.post(
      `${BASE_URL}/original_image/update-confirmed`,
      {
        ids,
        confirmed
      }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update confirmed status');
  };

  const handleStatusChange = async (value, record) => {
    const newPursuableValue = value === 'pending';
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await updatePursuableStatus([record.key], newPursuableValue);
      message.success(
        `Status updated to ${
          newPursuableValue ? 'Active' : 'Not Pursuable'
        } successfully.`
      );
    } catch (error) {
      message.error('Failed to update status. Please try again.');
    } finally {
      setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 500);
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleConfirmedChange = async (checked, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await updateConfirmedStatus([record.key], checked);
      message.success(
        `Image marked as ${checked ? 'confirmed' : 'unconfirmed'} successfully.`
      );
    } catch (error) {
      message.error('Failed to update confirmation status. Please try again.');
    } finally {
      setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 500);
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedKeys.length || bulkStatus === null) return;
    setBulkUpdating(true);
    try {
      const pursuable = bulkStatus === 'pending';
      await updatePursuableStatus(selectedKeys, pursuable);
      message.success(
        `${selectedKeys.length} image(s) updated to ${
          pursuable ? 'Active' : 'Not Pursuable'
        }.`
      );
      setSelectedKeys([]);
      setBulkStatus(null);
    } catch (error) {
      message.error('Failed to update some images. Please try again.');
    } finally {
      await setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 1000);
      setBulkUpdating(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedAgency(null);
    setSelectedStatus('pending');
    setShowConfirmedImages(false);
    setSortByLastModify(false);
    setImageId('');
    setSortState({ field: null, order: null });
    setSelectedKeys([]);
    setPagination((prev) => ({ ...prev, current: 1 }));
    searchOriginalImages({
      page: 0,
      size: pagination.pageSize,
      pursuable: true,
      confirmed: false
    });
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    let newSort = sortState;

    if (sorter && sorter.field) {
      newSort = sorter.order
        ? {
            field: sorter.field,
            order: sorter.order === 'ascend' ? 'asc' : 'desc'
          }
        : { field: null, order: null };
      setSortState(newSort);
    }

    const params = buildSearchParams({
      page: newPagination.current - 1,
      size: newPagination.pageSize
    });

    // Override sort with new sorter if changed
    if (newSort.field && newSort.order) {
      params.sort = `${newSort.field},${newSort.order}`;
      delete params.excludeNullDates;
    } else if (!sortByLastModify) {
      params.sort = 'sightingsCount,desc';
    }

    setPagination(newPagination);
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const columns = [
    {
      title: 'Image ID',
      dataIndex: 'imageId',
      key: 'imageId',
      width: 120
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (imageUrl) => (
        <img
          src={imageUrl}
          alt='Preview'
          style={{
            width: '100%',
            objectFit: 'cover',
            borderRadius: '4px',
            border: '1px solid #d9d9d9'
          }}
        />
      )
    },
    {
      title: 'Number of sightings',
      dataIndex: 'sightingsCount',
      key: 'sightingsCount',
      width: 160,
      sorter: true,
      sortOrder:
        sortState.field === 'sightingsCount'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: 'Not identical',
      dataIndex: 'notIdenticalSightingsCount',
      key: 'notIdenticalSightingsCount',
      width: 130,
      sorter: true,
      sortOrder:
        sortState.field === 'notIdenticalSightingsCount'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: 'Not identical ratio',
      dataIndex: 'notIdenticalRatio',
      key: 'notIdenticalRatio',
      width: 150,
      sorter: true,
      sortOrder:
        sortState.field === 'notIdenticalRatio'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      render: (val) => (val !== null && val !== undefined ? val : '—')
    },
    {
      title: 'Agency',
      dataIndex: 'agency',
      key: 'agency',
      width: 120
    },
    {
      title: 'Image/sighting status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => (
        <Select
          value={status}
          onChange={(value) => handleStatusChange(value, record)}
          style={{ width: '100%' }}
          size='small'
          loading={updatingStatus.has(record.key)}
          disabled={updatingStatus.has(record.key)}
        >
          <Option value='pending'>Active</Option>
          <Option value='approved'>Not Pursuable</Option>
        </Select>
      )
    },
    {
      title: 'Confirmed',
      dataIndex: 'confirmed',
      key: 'confirmed',
      width: 120,
      render: (confirmed, record) => (
        <Checkbox
          checked={confirmed}
          onChange={(e) => handleConfirmedChange(e.target.checked, record)}
          disabled={updatingStatus.has(record.key)}
        >
          {updatingStatus.has(record.key) ? 'Updating...' : 'Confirmed'}
        </Checkbox>
      )
    }
  ];

  return (
    <div className='image-management-page'>
      <div className='page-content'>
        <div className='filters-section'>
          <div className='filter-item'>
            <label className='filter-label'>Image ID</label>
            <Input
              placeholder='Enter image ID'
              value={imageId}
              onChange={(e) => setImageId(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '200px' }}
            />
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Agency</label>
            <Select
              placeholder='Select Agency'
              value={selectedAgency}
              onChange={handleAgencyChange}
              style={{ width: '200px' }}
              allowClear
              loading={loadingAgencies}
              showSearch
              filterOption={(input, option) =>
                option &&
                option.props &&
                option.props.children &&
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {agencies.map((agency, index) => (
                <Option key={index} value={agency}>
                  {agency}
                </Option>
              ))}
            </Select>
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Status</label>
            <Select
              placeholder='Select Status'
              value={selectedStatus}
              onChange={handleFilterStatusChange}
              style={{ width: '200px' }}
              allowClear
            >
              <Option value='pending'>Active</Option>
              <Option value='approved'>NotPursuable</Option>
            </Select>
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Options</label>
            <div className='checkbox-wrapper'>
              <Checkbox
                checked={showConfirmedImages}
                onChange={(e) => setShowConfirmedImages(e.target.checked)}
              >
                Show all confirmed Images
              </Checkbox>
              <Checkbox
                checked={sortByLastModify}
                onChange={(e) => handleSortByLastModifyChange(e.target.checked)}
                style={{ marginLeft: '16px' }}
              >
                Sort by last modified
              </Checkbox>
            </div>
          </div>

          <div className='filter-item'>
            <label className='filter-label' style={{ visibility: 'hidden' }}>
              Actions
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type='primary'
                onClick={handleSearch}
                style={{ flex: 1 }}
                size='default'
                loading={loading}
              >
                Search
              </Button>
              <Button
                onClick={handleClearFilters}
                style={{ flex: 1 }}
                size='default'
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <div className='content-section'>
          {selectedKeys.length > 0 && (
            <div className='bulk-action-bar'>
              <span className='bulk-action-bar__count'>
                {selectedKeys.length} image(s) selected
              </span>
              <Select
                placeholder='Set status'
                value={bulkStatus}
                onChange={(val) => setBulkStatus(val)}
                style={{ width: 180 }}
                size='small'
              >
                <Option value='pending'>Active</Option>
                <Option value='approved'>Not Pursuable</Option>
              </Select>
              <Button
                type='primary'
                size='small'
                loading={bulkUpdating}
                disabled={bulkStatus === null}
                onClick={handleBulkUpdate}
              >
                Apply
              </Button>
              <Button size='small' onClick={() => setSelectedKeys([])}>
                Clear selection
              </Button>
            </div>
          )}

          <Table
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys),
              getCheckboxProps: (record) => ({
                disabled: updatingStatus.has(record.key)
              }),
              columnWidth: 32
            }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['20', '50', '100', '500', '1000']
            }}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            size='middle'
            bordered
          />
        </div>
      </div>
    </div>
  );
};

export default ImageManagementPage;
